/**
 * ============================================================================
 * MOBILE MONEY PAYMENT COMPONENT - 100% Ready (Cameroon Focus)
 * ============================================================================
 * 
 * Mobile Money Payment for MTN Mobile Money and Orange Money (Cameroon)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePayment } from '../../hooks/usePayment';
import { useTransaction } from '../../hooks/useTransaction';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Input from '../common/Input';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import { validateCameroonPhone, formatPhoneInput, getFullPhoneNumber } from '../../utils/phoneValidation';

/**
 * Mobile Money Payment Component
 * @param {Object} props
 * @param {string} props.method - Payment method ('mtn' or 'orange')
 * @returns {JSX.Element}
 */
const MobileMoneyPayment = ({ method = 'mtn' }) => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const { initiatePayment, getPaymentStatus, loading } = usePayment();
  const { getTransaction, loading: transactionLoading } = useTransaction();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, language } = useI18n();

  const [formData, setFormData] = useState({
    phone: '',
  });

  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [errors, setErrors] = useState({});
  const [transaction, setTransaction] = useState(null);
  const [amount, setAmount] = useState(0);

  const methodInfo = {
    mtn: {
      name: 'MTN Mobile Money',
      icon: '📱',
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      borderColor: 'border-yellow-300 dark:border-yellow-700',
      ussd: '*126#',
      instructions: language === 'fr' 
        ? 'Composez *126# sur votre téléphone MTN et suivez les instructions'
        : 'Dial *126# on your MTN phone and follow the instructions',
    },
    orange: {
      name: 'Orange Money',
      icon: '🍊',
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      borderColor: 'border-orange-300 dark:border-orange-700',
      ussd: '#144#',
      instructions: language === 'fr'
        ? 'Composez #144# sur votre téléphone Orange et suivez les instructions'
        : 'Dial #144# on your Orange phone and follow the instructions',
    },
  };

  const currentMethod = methodInfo[method] || methodInfo.mtn;

  // Load transaction details – use backend totalAmount (amount + platform fee) so buyer pays exactly that
  useEffect(() => {
    const loadTransaction = async () => {
      if (!transactionId) return;

      try {
        const transactionData = await getTransaction(transactionId);
        const tx = transactionData?.data?.transaction || transactionData?.data || transactionData;
        setTransaction(tx);

        const expectedTotal = tx?.totalAmount;
        if (expectedTotal != null && !isNaN(parseFloat(expectedTotal))) {
          setAmount(parseFloat(expectedTotal));
        } else {
          const transactionAmount = tx?.amount || 0;
          const fee = Math.min(transactionAmount * 0.015, 15000);
          setAmount(transactionAmount + fee);
        }
      } catch (error) {
        console.error('Load transaction error:', error);
        showError(language === 'fr' ? 'Échec du chargement des détails de la transaction' : 'Failed to load transaction details');
      }
    };

    loadTransaction();
  }, [transactionId, getTransaction, showError, language]);

  const handleChange = (field, value) => {
    if (field === 'phone') {
      const formatted = formatPhoneInput(value, '+237');
      setFormData((prev) => ({ ...prev, [field]: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.phone.trim()) {
      newErrors.phone = language === 'fr' ? 'Le numéro de téléphone est requis' : 'Phone number is required';
    } else {
      const validation = validateCameroonPhone(formData.phone);
      if (!validation.valid) {
        newErrors.phone = validation.error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInitiatePayment = async () => {
    if (!validate()) {
      return;
    }

    try {
      setPolling(true);
      
      // Format phone number
      const phoneNumber = getFullPhoneNumber(formData.phone, '+237');

      // Initiate payment
      const payment = await initiatePayment({
        transactionId,
        method: method === 'mtn' ? 'mtn_mobile_money' : 'orange_money',
        amount,
        phone: phoneNumber,
      });

      setPaymentStatus(payment);
      showSuccess(
        language === 'fr' 
          ? 'Demande de paiement envoyée! Vérifiez votre téléphone.'
          : 'Payment request sent! Please check your phone.'
      );

      // Start polling for payment status
      startPolling(payment.id);
    } catch (error) {
      const msg = (typeof error === 'object' && error?.message) ? error.message : (language === 'fr' ? 'Échec de l\'initiation du paiement' : 'Failed to initiate payment');
      showError(msg);
      setPolling(false);
    }
  };

  const startPolling = async (paymentId) => {
    const maxAttempts = 30; // 5 minutes max
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPolling(false);
        showError(language === 'fr' ? 'Délai d\'attente expiré. Veuillez réessayer.' : 'Payment timeout. Please try again.');
        return;
      }

      try {
        const status = await getPaymentStatus(paymentId);
        
        if (status.status === 'completed') {
          setPolling(false);
          showSuccess(language === 'fr' ? 'Paiement réussi!' : 'Payment successful!');
          navigate(`/payment/success/${paymentId}`);
        } else if (status.status === 'failed' || status.status === 'cancelled') {
          setPolling(false);
          showError(language === 'fr' ? 'Le paiement a échoué. Veuillez réessayer.' : 'Payment failed. Please try again.');
        } else {
          attempts++;
          setTimeout(poll, 10000); // Poll every 10 seconds
        }
      } catch (error) {
        attempts++;
        setTimeout(poll, 10000);
      }
    };

    poll();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 animate-fadeInUp">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors group"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">{language === 'fr' ? 'Retour' : 'Back'}</span>
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentMethod.color} flex items-center justify-center text-2xl shadow-lg`}>
              {currentMethod.icon}
            </div>
            <h1 className="text-3xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              {currentMethod.name}
            </h1>
          </div>
        </div>

        {/* Amount Display */}
        <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <div className="text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2 font-medium">
              {language === 'fr' ? 'Montant à payer' : 'Amount to Pay'}
            </div>
            <div className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              {formatCurrency(amount, 'XAF')}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {language === 'fr' ? 'Incluant les frais' : 'Including fees'}
            </div>
          </div>
        </Card>

        {/* Payment Processing State */}
        {polling ? (
          <Card variant="glass" className="p-12 text-center animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <Spinner size="lg" className="mb-4" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {language === 'fr' ? 'Traitement du paiement...' : 'Processing payment...'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {language === 'fr' 
                ? 'Veuillez compléter la demande sur votre téléphone'
                : 'Please complete the prompt on your phone'}
            </p>
            <div className="mt-6">
              <div className="inline-block animate-pulse">
                <div className="w-2 h-2 bg-blue-600 rounded-full inline-block mx-1"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full inline-block mx-1"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full inline-block mx-1"></div>
              </div>
            </div>
          </Card>
        ) : (
          <>
            {/* Phone Number Input */}
            <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              <label className="block text-sm font-bold text-gray-900 dark:text-white mb-3">
                {language === 'fr' ? 'Numéro de téléphone' : 'Phone Number'}
              </label>
              <div className="flex gap-2">
                <span className="px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold">
                  +237
                </span>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="677 123 456"
                  error={errors.phone}
                  required
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {language === 'fr' ? 'Format: 9 chiffres commençant par 6, 7, 8 ou 9' : 'Format: 9 digits starting with 6, 7, 8, or 9'}
              </p>
            </Card>

            {/* USSD Instructions */}
            <Card variant="outlined" className={`mb-6 ${currentMethod.bgColor} ${currentMethod.borderColor} animate-fadeInUp`} style={{ animationDelay: '0.3s' }}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${currentMethod.color} flex items-center justify-center text-xl flex-shrink-0`}>
                  {currentMethod.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                    {language === 'fr' ? 'Instructions USSD' : 'USSD Instructions'}
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                    {currentMethod.instructions}
                  </p>
                  <div className="bg-white/80 dark:bg-gray-800/80 rounded-lg p-3">
                    <div className="font-mono text-lg font-bold text-gray-900 dark:text-white">
                      {currentMethod.ussd}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Initiate Payment Button */}
            <Button
              variant="gradient"
              size="lg"
              fullWidth
              onClick={handleInitiatePayment}
              loading={loading}
              className="text-lg py-4 mb-4 shadow-xl hover:shadow-2xl transition-all animate-fadeInUp"
              style={{ animationDelay: '0.4s' }}
            >
              {language === 'fr' ? 'Initier le paiement' : 'Initiate Payment'} →
            </Button>

            {/* Security Badge */}
            <Card variant="glass" className="text-center animate-fadeInUp" style={{ animationDelay: '0.5s' }}>
              <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
                <span className="text-xl">🔒</span>
                <p className="text-sm font-medium">
                  {language === 'fr' ? 'Paiement sécurisé via WekaTrust Escrow' : 'Secure payment via WekaTrust Escrow'}
                </p>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileMoneyPayment;
